<template>
  <Transition appear name="toast">
    <div
      v-if="isVisible"
      :class="props.type"
      aria-describedby="toast-description"
      aria-labelledby="toast-title"
      class="container"
      role="alert"
    >
      <div class="type-icon">
        <component :is="typeIconComponent" aria-hidden="true" />
      </div>
      <div
        :class="{ 'without-description': !props.description }"
        class="text-container"
      >
        <p id="toast-title" class="title" v-html="safeTitle" />
        <p
          id="toast-description"
          class="description"
          v-html="safeDescription"
        />
      </div>

      <button
        :aria-label="closeIconAltText"
        class="close-button"
        @click.prevent="handleOnClose"
      >
        <ExitIcon aria-hidden="true" />
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import '@/v2/design-system/styles/main.css';
import DOMPurify from 'dompurify';
import { FADE_OUT_DURATION_MS } from '@/stores/constants.js';
import ExitIcon from '@/v2/design-system/assets/icons/ExitIcon.vue';
import InfoIcon from '@/v2/design-system/assets/icons/InfoIcon.vue';
import SuccessIcon from '@/v2/design-system/assets/icons/SuccessIcon.vue';
import WarningIcon from '@/v2/design-system/assets/icons/WarningIcon.vue';
import ErrorIcon from '@/v2/design-system/assets/icons/ErrorIcon.vue';

const props = defineProps({
  type: {
    type: String,
    validator: function (value) {
      return ['info', 'success', 'warning', 'error'].indexOf(value) !== -1;
    },
    required: true
  },
  title: { type: String },
  description: { type: String },
  onClose: { type: Function, required: true },
  timer: { type: Number, default: 5000 },
  closeIconAltText: String
});

// isVisible serves as a buffer that allows the CSS transition to complete before the component is actually removed from the DOM
const isVisible = ref(true);

const autoCloseTimer = ref(null);
const fadeOutTimer = ref(null);

const safeTitle = computed(() => {
  return DOMPurify.sanitize(props.title);
});

const safeDescription = computed(() => {
  return DOMPurify.sanitize(props.description);
});

const typeIconComponent = computed(() => {
  switch (props.type) {
    case 'info':
      return InfoIcon;
    case 'error':
      return ErrorIcon;
    case 'success':
      return SuccessIcon;
    case 'warning':
      return WarningIcon;
    default:
      throw new Error(`Unsupported type ${props.type}`);
  }
});

const handleOnClose = () => {
  if (autoCloseTimer.value) {
    clearTimeout(autoCloseTimer.value);
    autoCloseTimer.value = null;
  }

  if (fadeOutTimer.value) {
    clearTimeout(fadeOutTimer.value);
    fadeOutTimer.value = null;
  }

  isVisible.value = false;

  fadeOutTimer.value = setTimeout(() => {
    if (props.onClose) {
      props?.onClose();
    }
  }, FADE_OUT_DURATION_MS);
};

onMounted(() => {
  if (Number.isFinite(props.timer)) {
    autoCloseTimer.value = setTimeout(() => {
      handleOnClose();
    }, props.timer);
  }
});

onUnmounted(() => {
  // Clean up timers when component is destroyed
  if (autoCloseTimer.value) {
    clearTimeout(autoCloseTimer.value);
  }
  if (fadeOutTimer.value) {
    clearTimeout(fadeOutTimer.value);
  }
});

// only for styles
const fadeOutDuration = `${FADE_OUT_DURATION_MS}ms`;
</script>

<style scoped>
.container {
  border-radius: var(--corner-radius-c-12);
  border: var(--border-width-b-1) solid var(--neutral-border-subtle);
  background: var(--neutral-surface-subtle);

  width: calc(100vw - 1.38em);

  font-family: var(--font-family-body), sans-serif;
  padding: var(--pad-space-sp-16);
  display: inline-flex;
  align-items: flex-start;
  gap: 16px;

  box-shadow: var(--outer-shadow-s-1-x, 0px) var(--outer-shadow-s-1-y, 4px)
    var(--outer-shadow-s-1-blur, 12px) 0 rgba(196, 196, 200, 0.24);
}

.container.info {
  & .title {
    color: var(--primary-text-icon-default);
  }

  & > .type-icon {
    background-color: var(--primary-surface-subtle);
    color: var(--primary-text-icon-default);
  }
}

.container.success {
  & .title {
    color: var(--success-text-icon-default);
  }

  & > .type-icon {
    background-color: var(--success-surface-subtle);
    color: var(--success-text-icon-default);
  }
}

.container.warning {
  & .title {
    color: var(--warning-text-icon-default);
  }

  & > .type-icon {
    background-color: var(--warning-surface-subtle);
    color: var(--warning-text-icon-default);
  }
}

.container.error {
  & .title {
    color: var(--neutral-text-icon-default);
  }

  & > .type-icon {
    background-color: var(--error-surface-subtle);
    color: var(--error-text-icon-default);
  }
}

.text-container {
  display: flex;
  flex-direction: column;

  p {
    margin: 0;
  }
}

.text-container.without-description {
  align-self: center;
}

.type-icon {
  padding: var(--pad-space-sp-8);
  border-radius: var(--corner-radius-c-6);
  display: flex;

  & > svg {
    height: var(--icon-size-i-24);
    width: var(--icon-size-i-24);
  }
}

.close-button {
  color: var(--neutral-text-icon-default);
  margin-left: auto;

  & > svg {
    height: var(--icon-size-i-16);
    width: var(--icon-size-i-16);
  }
}

.title {
  font-size: var(--body-base-bold-size);
  font-weight: var(--body-base-bold-weight);
  color: var(--primary-text-icon-default);
  line-height: var(--body-base-reg-line-height);
}

.description {
  color: var(--neutral-text-icon-subtitle);
  font-size: var(--body-small-reg-size);
  font-weight: var(--body-small-reg-weight);
  line-height: var(--body-base-reg-line-height);
}

.toast-enter-active {
  transition: opacity v-bind(fadeOutDuration) ease-in;
}

.toast-leave-active {
  transition: opacity v-bind(fadeOutDuration) ease-out;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
}

.toast-enter-to,
.toast-leave-from {
  opacity: 1;
}

@media (min-width: 480px) {
  .container {
    width: 428px;
  }
}
</style>
